// src/components/wellfinder/MapLegend.jsx
//
// Decodes the frame without leaving it. Not dismissible and not behind a
// setting: it is five lines, it is always useful, and the encoding is not
// self-evident — a neutral grey dot and a teal dot differ by rank, which is
// nothing an analyst could infer.
//
// Every swatch is DERIVED from reviewMapEncoding.js, the same object the map's
// paint expressions are built from. A hand-written legend is one that quietly
// stops being true, and a legend that lies about what orange means is worse
// than no legend at all.

import { LEGEND_ITEMS } from "./reviewMapEncoding";

function Swatch({ item }) {
  // Lines have a stroke and no fill; everything else is a point. Drawing a
  // line as a dot would misrepresent an encoding whose shape carries meaning.
  if (item.fill == null) {
    return (
      <span
        aria-hidden="true"
        data-testid={`legend-swatch-${item.key}`}
        data-color={item.stroke}
        className="inline-block h-0 w-3.5 shrink-0 align-middle"
        style={{
          // Dashed for the link, solid for the footprint — the dash is itself
          // an encoding: there is no line in the world between a detection and
          // a registry point, and a solid one would read as a real feature.
          borderTop: `${item.width * 2}px ${item.dash ? "dashed" : "solid"} ${item.stroke}`,
          opacity: item.opacity,
        }}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      data-testid={`legend-swatch-${item.key}`}
      data-color={item.fill}
      className="inline-block shrink-0 rounded-full align-middle"
      style={{
        // Scaled down from the map radii so five swatches fit on five tight
        // lines while keeping their relative order — rank 1 bigger than the
        // rest, active bigger still.
        width: item.radius + 4,
        height: item.radius + 4,
        backgroundColor: item.fill,
        boxShadow: `0 0 0 ${Math.min(item.strokeWidth, 2)}px ${item.stroke}`,
      }}
    />
  );
}

// `showHolder`: the holder entries describe marks that exist only while the
// analyst has asked to see who holds a taken candidate. Listing them the rest
// of the time would explain a yellow nothing and cost two lines of a legend
// that sits on a 480px map.
export default function MapLegend({ showHolder = false }) {
  const items = LEGEND_ITEMS.filter(
    (item) => !item.onlyWith || (item.onlyWith === "holder" && showHolder),
  );
  return (
    <div
      data-testid="map-legend"
      className="pointer-events-none absolute bottom-2 left-2 rounded border border-bg-border bg-bg/85 px-2 py-1.5 backdrop-blur-sm"
    >
      <ul className="space-y-1">
        {items.map((item) => (
          <li
            key={item.key}
            data-testid={`legend-item-${item.key}`}
            className="flex items-center gap-2 text-[10px] leading-none text-fg-secondary"
          >
            <span className="flex w-5 justify-center">
              <Swatch item={item} />
            </span>
            {item.label}
          </li>
        ))}
      </ul>
    </div>
  );
}
